import * as Dialog from '@radix-ui/react-dialog';
import { ChevronRight, Loader2 } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { useToast } from '@/components/ui/Toast';
import { ROUTES, STORAGE_KEYS } from '@/constants';
import { taskcontants, TaskSectionType } from '@/constants/taskConstants';
import { useLogoutMutation } from '@/hooks/auth';
import { UpdateUserLogAsync } from '@/service/AuthenticationService';
import {
  GetAllTaskToRelease,
  GetTaskListAsync,
  GetTaskSummaryAsync,
  UpdateTaskAsync,
} from '@/service/ManhattanService';
import { UserLogRequestModel } from '@/service/types/authenticatetypes';
import { store } from '@/store';
import { cn } from '@/utils/cn';
import { isTaskInCurrentSession } from '@/utils/dateUtils';

import type { TaskSummaryDetailItem, TaskSummaryModel } from '@/service/types/audittasktypes';
import type { GetTaskResponseDetailItem } from '@/types';

export function LogoutSummaryPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const logoutMutation = useLogoutMutation();
  // TODO: Wire _isLoading to loading spinner UI
  const [isLoading, setIsLoading] = useState(true);
  const [taskSummary, setTaskSummary] = useState<TaskSummaryModel | null>(null);
  const [completedTasks, setCompletedTasks] = useState<TaskSummaryDetailItem[]>([]);
  const [inProgressTasks, setInProgressTasks] = useState<TaskSummaryDetailItem[]>([]);
  const [acceptedTasks, setAcceptedTasks] = useState<TaskSummaryDetailItem[]>([]);
  const [criticalCount, setCriticalCount] = useState(0);
  const [highPriorityCount, setHighPriorityCount] = useState(0);
  const [moveCompleted, setMoveCompleted] = useState(0);
  const [auditCompleted, setAuditCompleted] = useState(0);
  // const [logoutLoader, setLogoutLoader] = useState(false)
  const { showToast } = useToast();

  useEffect(() => {
    void initdata();
  }, []);

  function mapTaskDetailToSummaryItem(item: GetTaskResponseDetailItem): TaskSummaryDetailItem {
    return {
      ActionCode: item.ActionCode,
      OrgId: item.OrgId,
      TaskId: item.TaskId,
      TaskType: item.TaskType,
      Status: item.Status,
      AssignedUserId: item.AssignedUserId,
      CreatedSource: item.CreatedSource,
      CreatedDatetime: item.CreatedDatetime,
      LastUpdatedDatetime: item.LastUpdatedDatetime,
      LastUpdatedSource: item.LastUpdatedSource,
      Priority: item.Priority,
      YardId: item.YardId,
      TaskStartDatetime: item.TaskStartDateTime,
      TaskEndDatetime: item.TaskEndDateTime,
      TaskReleaseDatetime: item.TaskReleaseDatetime,
      TaskComments: item.TaskComments,
      TrailerId: item.TrailerId,
      TrailerName: item.TrailerName,
      TrailerComments: item.TrailerComments,
      CarrierCompanyName: item.CarrierCompanyName,
      CarrierName: item.CarrierName,
      AuditLocationType: item.AuditLocationType,
      AuditLocationZone: item.AuditLocationZone,
      AuditLocationName: item.AuditLocationName,
      MoveSourceType: item.MoveSourceType,
      MoveSourceZone: item.MoveSourceZone,
      MoveSourceName: item.MoveSourceName,
      MoveDestinationType: item.MoveDestinationType,
      MoveDestinationZone: item.MoveDestinationZone,
      MoveDestinationName: item.MoveDestinationName,
      DisplayCreatedDatetime: item.CreatedDatetime,
      DisplayLastUpdatedDatetime: item.LastUpdatedDatetime,
      ExceptionCode: item.ExceptionCode,
      TaskAssignedTime: item.TaskAssignedTime,
      TaskDescription: '',
    };
  }

  const initdata = async () => {
    setIsLoading(true);
    try {
      const [taskSummaryRes, myTaskListRes, inProgressListRes] = await Promise.all([
        GetTaskSummaryAsync(),
        GetTaskListAsync(TaskSectionType.MyTaskList).catch(() => null),
        GetTaskListAsync(TaskSectionType.OnGoingTask).catch(() => null),
      ]);

      if (taskSummaryRes) {
        setTaskSummary(taskSummaryRes);
        const taskDetails = taskSummaryRes.payload.Body.Task.TaskDetails;
        const completedTask = taskDetails.filter(
          (x) => x.Status.toLowerCase() === taskcontants.CompleteTaskStatus.toLowerCase()
        );

        const completed = completedTask.filter((task) =>
          isTaskInCurrentSession(task.LastUpdatedDatetime)
        );
        const critical = completed.filter(
          (x) => x.Priority === taskcontants.PriorityCriticalValue
        ).length;
        const highPriority = completed.filter(
          (x) => x.Priority === taskcontants.PriorityHighValue
        ).length;
        const moveCount = completed.filter(
          (x) => x.TaskType.toLowerCase() === taskcontants.MoveTaskType.toLowerCase()
        ).length;
        const auditCount = completed.filter(
          (x) => x.TaskType.toLowerCase() === taskcontants.AuditTaskType.toLowerCase()
        ).length;

        setCompletedTasks(completed);
        setCriticalCount(critical);
        setHighPriorityCount(highPriority);
        setMoveCompleted(moveCount);
        setAuditCompleted(auditCount);
      } else {
        setTaskSummary(null);
        setCompletedTasks([]);
        setCriticalCount(0);
        setHighPriorityCount(0);
        setMoveCompleted(0);
        setAuditCompleted(0);
      }

      const liveAccepted = myTaskListRes?.Payload.Body.Task.TaskDetails ?? [];
      setAcceptedTasks(liveAccepted.map(mapTaskDetailToSummaryItem));

      const liveInProgress = inProgressListRes?.Payload.Body.Task.TaskDetails ?? [];
      setInProgressTasks(liveInProgress.map(mapTaskDetailToSummaryItem));
    } catch {
      setTaskSummary(null);
      setCompletedTasks([]);
      setInProgressTasks([]);
      setAcceptedTasks([]);
      setCriticalCount(0);
      setHighPriorityCount(0);
      setMoveCompleted(0);
      setAuditCompleted(0);
    }
    setIsLoading(false);
  };

  const handleCancel = useCallback(() => {
    navigate(ROUTES.dashboard);
  }, [navigate]);

  const handleLogout = async () => {
    // setLogoutLoader(true)
    try {
      if (acceptedTasks.length > 0 || inProgressTasks.length > 0) {
        await ReleaseAllTasks();
      } else {
        await OnSuccessfulTaskRelease();
      }
    } catch {
      // Logout error handled by mutation callbacks below
    }

    logoutMutation.mutate(undefined, {
      onSuccess: () => {
        navigate(ROUTES.login, { replace: true });
      },
      onError: () => {
        // Force cleanup even on error
        Object.values(STORAGE_KEYS).forEach((key) => {
          localStorage.removeItem(key);
        });
        navigate(ROUTES.login, { replace: true });
      },
    });
  };

  const ReleaseAllTasks = async () => {
    try {
      // if (_connectivity.NetworkAccess != NetworkAccess.Internet)
      // {
      //     _dialogService.ShowToast(AppResources.NoInternetMessage, ToastType.Warning);
      //     return;
      // }

      const updateTaskRequest = await GetAllTaskToRelease();

      const cancelTaskResponse = await UpdateTaskAsync(updateTaskRequest);

      if (
        cancelTaskResponse.payload.Body.Status == taskcontants.SuccessStatus &&
        cancelTaskResponse.payload.Body.UpdateTaskDetails[0]?.Status == taskcontants.SuccessStatus
      ) {
        await OnSuccessfulTaskRelease();
      } else {
        if (cancelTaskResponse.payload.Body.UpdateTaskDetails.length > 0) {
          const failedTasks = cancelTaskResponse.payload.Body.UpdateTaskDetails.filter(
            (x) => x.Status != taskcontants.SuccessStatus
          );
          for (const task of failedTasks) {
            if (
              task.Status != taskcontants.SuccessStatus &&
              task.StatusMessage.includes(task.TaskId) &&
              (task.StatusMessage.toLowerCase().includes(
                taskcontants.CompleteTaskStatus.toLowerCase()
              ) ||
                task.StatusMessage.toLowerCase().includes(taskcontants.CancelledTaskStatus))
            ) {
              await OnSuccessfulTaskRelease();
              return;
            } else {
              showToast(task.StatusMessage, { type: 'warning' });
              return;
            }
          }
        }
      }
    } catch {
      // Release tasks error — continue with logout flow
    }
  };

  const OnSuccessfulTaskRelease = async () => {
    try {
      const state = store.getState();
      await LogUserLogout();
      localStorage.clear();
      const deviceId = state.global.config?.device._id ?? '';
      localStorage.setItem('device_id', deviceId);
      navigate(ROUTES.login);
    } catch {
      // Task release cleanup error
    }
  };

  const LogUserLogout = async () => {
    // const settings = store.getState().global.settings;
    // const state = store.getState();
    // const orgId = String(state.user.plant?.org_id ?? '');
    // const userId = `${state.user.user?.country_code ?? ''}${state.user.user?.mobile_no ?? ''}`;
    const userId = store.getState().user.user?._id ?? '';
    const plantId = store.getState().user.plant?._id ?? '';
    const sessionId = store.getState().user.sessionId ?? '';

    const userLogs: UserLogRequestModel = {
      user: userId,
      plant_id: plantId,
      status: taskcontants.UserStatusEndYardWork,
      activity_date_time: new Date().toISOString(),
      session_id: sessionId,
    };
    await UpdateUserLogAsync(userLogs);

    const userLog: UserLogRequestModel = {
      user: userId,
      plant_id: plantId,
      status: taskcontants.UserStatusLogout,
      activity_date_time: new Date().toISOString(),
      session_id: sessionId,
    };
    await UpdateUserLogAsync(userLog);
  };

  interface TotalTasksCardProps {
    completed: number;
    total: number | null;
  }

  function TotalTasksCard({ completed, total }: TotalTasksCardProps) {
    const { t } = useTranslation();
    const percentage = total ? (total > 0 ? (completed / total) * 100 : 0) : 0;

    return (
      <div className="bg-[#003366] rounded-xl py-6 px-4 text-[#e0e0e0] w-[230px] min-w-[200px] h-[150px]">
        <p className="text-4xl font-bold text-[#4CD964]">{completed}</p>
        <p className="text-xs mt-5">
          {t('logoutSummary.totalTasksDone')}{' '}
          <span className="text-[#4CD964] font-bold">{total}</span>
        </p>
        <p className="text-[10px] text-white/70 mt-1 flex justify-end">
          {completed} {t('logoutSummary.tasksDone')}
        </p>
        <div className="mt-1 h-1 bg-gray-200 rounded-full overflow-hidden">
          <div
            className="h-full rounded-full transition-all bg-[#0c6ac9]"
            style={{ width: `${String(percentage)}%` }}
          />
        </div>
      </div>
    );
  }

  interface PriorityCardProps {
    label: string;
    count: number;
    borderColor: string;
    textColor: string;
  }

  function PriorityCard({ label, count, borderColor, textColor }: PriorityCardProps) {
    const { t } = useTranslation();
    return (
      <div
        className="flex items-center justify-center rounded-xl py-6 px-4 w-[230px] min-w-[200px] h-[150px]"
        style={{ border: `1.5px solid ${borderColor}` }}
      >
        <p className="text-base font-semibold text-center" style={{ color: textColor }}>
          {count > 0
            ? count > 1
              ? t('logoutSummary.taskCountPlural', { count, type: label.toLowerCase() })
              : t('logoutSummary.taskCount', { count, type: label.toLowerCase() })
            : t('logoutSummary.noTask', { type: label.toLowerCase() })}
        </p>
      </div>
    );
  }

  interface TaskTypeCardProps {
    label: string;
    completed: number;
    total: number | null;
    borderColor: string;
    icon?: React.ReactNode;
  }

  function TaskTypeCard({ label, completed, total, borderColor, icon }: TaskTypeCardProps) {
    const { t } = useTranslation();
    const percentage = total ? (total > 0 ? (completed / total) * 100 : 0) : 0;

    return (
      <div
        className="rounded-xl p-4 w-[230px] min-w-[200px] h-[150px]"
        style={{ border: `1.5px solid ${borderColor}` }}
      >
        <div className="flex items-start justify-between">
          <p className="text-lg font-bold text-gray-900">
            {completed}/{total}
          </p>
          {icon}
        </div>
        <p className="text-xs text-gray-900 mt-1">
          {t('logoutSummary.tasksAreDone', { count: 0, label, total: String(total) })}
        </p>
        <p className="flex justify-end text-[10px] text-gray-900 mt-4">
          {completed} {t('logoutSummary.tasksDone')}
        </p>
        <div className="mt-1 h-1 bg-gray-200 rounded-full overflow-hidden">
          <div
            className="h-full rounded-full transition-all"
            style={{ width: `${String(percentage)}%`, backgroundColor: borderColor }}
          />
        </div>
      </div>
    );
  }

  interface EmptyTypeCardProps {
    title: string;
    color: string;
  }

  function EmptyTypeCard({ title, color }: EmptyTypeCardProps) {
    const { t } = useTranslation();
    return (
      <div
        className="rounded-xl p-4 w-[230px] min-w-[200px] h-[150px] flex items-center justify-center"
        style={{ border: `1.5px solid ${color}` }}
      >
        <span className="text-center font-medium" style={{ color }}>
          {t('logoutSummary.noTask', { type: title })}
        </span>
      </div>
    );
  }

  function EmptyTaskContainer() {
    const { t } = useTranslation();
    return (
      <div className="flex flex-col items-center justify-center gap-5">
        <img src="/images/completed_empty.svg" alt="" className="h-88 w-88" aria-hidden="true" />
        <span className="items-center font-bold text-2xl">{t('logoutSummary.noTasks')}</span>
      </div>
    );
  }
  interface CollapsibleSectionProps {
    title: string;
    count: number;
    color: string;
    tasks: TaskSummaryDetailItem[];
  }

  function CollapsibleSection({ title, count, color, tasks }: CollapsibleSectionProps) {
    const [expanded, setExpanded] = useState(false);
    const [selectedTask, setSelectedTask] = useState<TaskSummaryDetailItem | null>(null);

    const handleToggle = useCallback(() => {
      setExpanded((prev) => !prev);
    }, []);

    const handleOpenDetail = useCallback((task: TaskSummaryDetailItem) => {
      setSelectedTask(task);
    }, []);

    const handleCloseDetail = useCallback(() => {
      setSelectedTask(null);
    }, []);
    return (
      <div
        className={cn(
          'border rounded-2xl overflow-hidden',
          expanded
            ? 'border-[#003366] px-4 pt-4 pb-4 md:px-4 md:pt-4 md:pb-4'
            : 'border-[#a0cce8] p-4 md:p-6'
        )}
      >
        <button
          onClick={handleToggle}
          className="w-full flex items-center justify-between bg-white"
        >
          <span className="text-lg font-medium" style={{ color }}>
            {title} ({count})
          </span>
          <div
            className={cn(
              'border p-[1.7px] rounded-full',
              expanded ? 'border-[#003366] bg-[#003366]' : 'border-[#d3dee8]'
            )}
          >
            <ChevronRight
              className={cn(
                'h-4 w-4 transition-transform',
                expanded ? 'rotate-90 text-white' : 'text-[#0b3c6e]'
              )}
            />
          </div>
        </button>
        {expanded && tasks.length > 0 ? (
          <div className="border border-blue-500 bg-gray-50 rounded-md mt-4 px-4 py-2">
            {tasks.map((task, index) => (
              <div
                key={task.TaskId}
                className={cn(index > 0 && 'mt-3 border-t border-gray-200 pt-3')}
              >
                <div className="flex items-center gap-2">
                  <div className="h-8 w-8 rounded-full flex-shrink-0 flex items-center justify-center">
                    {task.TaskType.toLowerCase() === 'move' ? (
                      <img
                        src="/images/move_small.svg"
                        alt=""
                        className="h-6 w-6"
                        aria-hidden="true"
                      />
                    ) : (
                      <img
                        src="/icons/audit_blue.svg"
                        alt=""
                        className="h-6 w-6"
                        aria-hidden="true"
                      />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <p className="text-base font-normal text-gray-800 truncate">
                        {t('dashboard.taskId')} {task.TaskId}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="flex justify-between items-start mt-1">
                  <p className="text-[14px] text-gray-800 min-w-0">
                    {task.TaskType === 'Move' && (
                      <>
                        {t('logoutSummary.moveTaskDescription')}{' '}
                        <span className="text-base font-bold">{task.TrailerName}</span>{' '}
                        {t('logoutSummary.from')}{' '}
                        <span className="font-bold">{task.MoveSourceName}</span>{' '}
                        {t('logoutSummary.to')}{' '}
                        <span className="font-bold">{task.MoveDestinationName}</span>
                      </>
                    )}
                    {task.TaskType === 'Audit' && (
                      <>
                        {t('logoutSummary.auditTaskDescription')}{' '}
                        <span className="text-base font-bold">{task.TrailerName}</span>{' '}
                        {t('logoutSummary.in')}{' '}
                        <span className="font-bold">{task.AuditLocationName}</span>
                      </>
                    )}
                  </p>
                  <button
                    type="button"
                    className="text-blue-400 cursor-pointer text-sm flex-shrink-0 ml-2 bg-transparent border-none p-0"
                    onClick={() => {
                      handleOpenDetail(task);
                    }}
                  >
                    {t('common.view')}
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          expanded && <EmptyTaskContainer />
        )}

        {/* Task Detail Dialog */}
        <Dialog.Root
          open={selectedTask !== null}
          onOpenChange={(open) => {
            if (!open) handleCloseDetail();
          }}
        >
          <Dialog.Portal>
            <Dialog.Overlay className="fixed inset-0 bg-black/40 z-50" />
            <Dialog.Content className="fixed left-1/2 top-1/2 z-50 w-[90%] max-w-lg max-h-[90%] -translate-x-1/2 -translate-y-1/2 rounded-2xl bg-white px-4 pt-4 shadow-xl focus:outline-none overflow-y-auto">
              {selectedTask && <TaskDetailDialog task={selectedTask} onClose={handleCloseDetail} />}
            </Dialog.Content>
          </Dialog.Portal>
        </Dialog.Root>
      </div>
    );
  }

  function TaskDetailDialog({
    task,
    onClose,
  }: {
    task: TaskSummaryDetailItem;
    onClose: () => void;
  }) {
    const { t } = useTranslation();
    return (
      <div className="flex flex-col">
        {/* Header */}
        <Dialog.Title className="flex items-center justify-center gap-2 text-lg font-semibold text-gray-900 mb-4">
          {task.TaskType.toLowerCase() === 'move' ? (
            <img src="/icons/move_details.svg" alt="" className="h-6 w-6" aria-hidden="true" />
          ) : (
            <img src="/icons/audit_details.svg" alt="" className="h-6 w-6" aria-hidden="true" />
          )}
          {t('common.details')}
        </Dialog.Title>

        <div
          className={cn(
            'flex flex-col border rounded-md',
            task.TaskType.toLowerCase() === 'move'
              ? 'border-[#e6cbae] bg-[#fff8f0]'
              : 'border-[#9fb8d1] bg-[#f2f6ff]'
          )}
        >
          {/* Banner */}
          <div
            className={cn(
              'flex gap-3 rounded-md px-4 py-3 mb-6',
              task.TaskType.toLowerCase() === 'move' ? 'bg-[#e38d24]' : 'bg-[#263e94]'
            )}
          >
            <div className="flex flex-col items-center text-white flex-shrink-0">
              {task.TaskType.toLowerCase() === 'move' ? (
                <img src="/icons/move_task.svg" alt="" className="h-6 w-6" aria-hidden="true" />
              ) : (
                <img src="/icons/audit_task.svg" alt="" className="h-6 w-6" aria-hidden="true" />
              )}
              <div className="font-semibold text-xs">{task.TaskType}</div>
            </div>
            <p className="text-white text-xs md:text-sm flex-1 min-w-0 break-words">
              {task.TaskType.toLowerCase() === 'move' && (
                <>
                  {t('logoutSummary.moveTaskDescription')}{' '}
                  <span className="font-bold">{task.TrailerName}</span> {t('logoutSummary.from')}{' '}
                  <span className="font-bold">{task.MoveSourceName}</span> {t('logoutSummary.to')}{' '}
                  <span className="font-bold">{task.MoveDestinationName}</span>
                </>
              )}
              {task.TaskType.toLowerCase() === 'audit' && (
                <>
                  {t('logoutSummary.auditTaskDescription')}{' '}
                  <span className="font-bold">{task.TrailerName}</span> {t('logoutSummary.in')}{' '}
                  <span className="font-bold">{task.AuditLocationName}</span>
                </>
              )}
            </p>
          </div>

          {/* Details Grid */}
          <div className="px-4 grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-4">
            <div>
              <p className="text-xs md:text-base font-light text-gray-900">
                {t('dashboard.taskId')}
              </p>
              <p className="text-xs md:text-sm text-gray-600 mt-0.5 break-words">{task.TaskId}</p>
            </div>
            <div>
              <p className="text-xs md:text-base font-light text-gray-900">
                {t('dashboard.trailerNumber')}
              </p>
              <p className="text-xs md:text-sm text-gray-600 mt-0.5 break-words">
                {task.TrailerName}
              </p>
            </div>
            <div>
              <p className="text-xs md:text-base font-light text-gray-900">
                {t('dashboard.carrierName')}
              </p>
              <p className="text-xs md:text-sm text-gray-600 mt-0.5 break-words">
                {task.CarrierCompanyName}
              </p>
            </div>
            <div>
              <p className="text-xs md:text-base font-light text-gray-900">
                {t('dashboard.sourceLocation')}
              </p>
              <p className="text-xs md:text-sm text-gray-600 mt-0.5 break-words">
                {task.MoveSourceName && task.MoveSourceType && task.MoveSourceZone
                  ? `${task.MoveSourceZone} - ${task.MoveSourceName} (${task.MoveSourceType})`
                  : '-'}
              </p>
            </div>
            {task.TaskType.toLowerCase() === 'move' && (
              <div>
                <p className="text-xs md:text-base font-light text-gray-900">
                  {t('dashboard.destinationLocation')}
                </p>
                <p className="text-xs md:text-sm text-gray-600 mt-0.5 break-words">
                  {task.MoveDestinationZone} - {task.MoveDestinationName} (
                  {task.MoveDestinationType})
                </p>
              </div>
            )}
            <div>
              <p className="text-xs md:text-base font-light text-gray-900">
                {t('dashboard.taskStartTime')}
              </p>
              <p className="text-xs md:text-sm text-gray-600 mt-0.5 break-words">
                {task.TaskStartDatetime ? task.TaskStartDatetime : '-'}
              </p>
            </div>
            <div>
              <p className="text-xs md:text-base font-light text-gray-900">
                {t('dashboard.taskEndTime')}
              </p>
              <p className="text-xs md:text-sm text-gray-600 mt-0.5 break-words">
                {task.TaskEndDatetime ? task.TaskEndDatetime : '-'}
              </p>
            </div>
            <div>
              <p className="text-xs md:text-base font-light text-gray-900">
                {t('dashboard.exceptionNote')}
              </p>
              <p className="text-xs md:text-sm text-gray-600 mt-0.5 break-words">
                {task.ExceptionCode ? task.ExceptionCode : '-'}
              </p>
            </div>
            {task.TaskType.toLowerCase() === 'move' && (
              <div>
                <p className="text-xs md:text-base font-light text-gray-900">
                  {t('dashboard.altLocation')}
                </p>
                <p className="text-xs md:text-sm text-gray-600 mt-0.5 break-words">
                  {task.MoveDestinationName}
                </p>
              </div>
            )}
          </div>

          {/* Trailer Comments */}
          <div className="mx-4 mt-4 min-h-[5rem] border border-black rounded-xl px-4 py-3 mb-1 break-words">
            <span className="text-xs md:text-base font-light text-gray-900">
              {t('dashboard.trailerComments')} :{' '}
            </span>
            <span className="text-xs md:text-sm text-gray-600 break-words">
              {task.TrailerComments}
            </span>
          </div>
        </div>
        {/* Close Button */}
        <div className="flex justify-center py-3">
          <Dialog.Close asChild>
            <button
              type="button"
              className="px-3 py-3 text-black bg-white border border-[#e6cbae] rounded-lg text-sm font-medium hover:bg-gray-100 hover:border-none"
              onClick={onClose}
            >
              {t('common.close')}
            </button>
          </Dialog.Close>
        </div>
      </div>
    );
  }

  return isLoading ? (
    <div className="flex items-center justify-center py-12">
      <Loader2 className="h-8 w-8 animate-spin text-[#1357A0]" />
    </div>
  ) : (
    <div className="flex flex-col h-[calc(100vh-3.5rem)] overflow-hidden">
      <div className="flex-1 overflow-y-auto p-4 md:p-6 scrollbar-thin">
        {/* Summary Cards Section */}
        <div className="border border-[#a0cce8] rounded-2xl p-4 md:p-6 mb-4">
          {/* Top row: Total, Critical, High Priority */}
          <div className="flex flex-wrap justify-around items-center gap-4">
            <TotalTasksCard
              completed={completedTasks.length}
              total={taskSummary ? Number(taskSummary.payload.Body.NumOfRecords) : null}
            />
            <PriorityCard
              label={t('logoutSummary.critical')}
              count={criticalCount}
              borderColor={'#fc3b3b'}
              textColor={'#fc4444'}
            />
            <PriorityCard
              label={t('logoutSummary.highPriority')}
              count={highPriorityCount}
              borderColor={'#edbe18'}
              textColor={'#edbe18'}
            />
          </div>

          {/* Bottom row: Move Tasks, Audit Tasks */}
          <div className="flex flex-wrap justify-center gap-20 mt-12">
            <TaskTypeCard
              label={t('logoutSummary.move')}
              completed={moveCompleted}
              total={taskSummary ? Number(taskSummary.payload.Body.NumOfMoves) : null}
              borderColor="#e38d24"
              icon={
                <div className="h-8 w-8 rounded-full flex items-center justify-center">
                  <img src="/images/move_small.svg" alt="" className="h-6 w-6" aria-hidden="true" />
                </div>
              }
            />
            {Number(taskSummary?.payload.Body.NumOfAudits) > 0 ? (
              <TaskTypeCard
                label={t('logoutSummary.audit')}
                completed={auditCompleted}
                total={taskSummary ? Number(taskSummary.payload.Body.NumOfAudits) : null}
                borderColor="#263e94"
              />
            ) : (
              <EmptyTypeCard title={t('logoutSummary.audit')} color="#263e94" />
            )}
          </div>
        </div>

        {/* Collapsible Task Sections */}
        <div className="space-y-4">
          <CollapsibleSection
            title={t('logoutSummary.completed')}
            count={completedTasks.length}
            color="#2fa851"
            tasks={completedTasks}
          />
          <CollapsibleSection
            title={t('logoutSummary.accepted')}
            count={acceptedTasks.length}
            color="#2f82ed"
            tasks={acceptedTasks}
          />
          <CollapsibleSection
            title={t('logoutSummary.inProgress')}
            count={inProgressTasks.length}
            color="#e38d24"
            tasks={inProgressTasks}
          />
        </div>
      </div>

      {(inProgressTasks.length > 0 || acceptedTasks.length > 0) && (
        <div className="flex justify-center w-full items-center py-10">
          <span className="rounded-md p-3 flex justify-center items-center w-fit bg-[#e3ad24] text-black">
            {t('logoutSummary.pendingTaskWarning')}
          </span>
        </div>
      )}
      {/* Footer with Cancel and Logout buttons */}
      <div className="flex-shrink-0 border-t border-gray-200 bg-white px-4 py-4 md:px-6">
        <div className="flex items-center justify-end gap-3 md:gap-5">
          <button
            className="flex-1 md:flex-none px-6 md:px-14 py-2 text-[#003366] bg-white border border-[#003366] hover:text-black hover:border-transparent hover:bg-[#e6e6e6] rounded-lg text-sm font-medium"
            onClick={handleCancel}
          >
            {t('common.cancel')}
          </button>
          <button
            className={cn(
              'flex-1 md:flex-none px-6 md:px-14 py-2 text-[#c8d3de] rounded-lg bg-[#003366]',
              logoutMutation.isPending
                ? 'opacity-50 cursor-not-allowed'
                : 'hover:text-black hover:bg-[#e6e6e6]'
            )}
            onClick={handleLogout}
            disabled={logoutMutation.isPending}
          >
            {logoutMutation.isPending ? t('common.loading') : t('logoutSummary.logout')}
          </button>
        </div>
      </div>
    </div>
  );
}
